import { useRouteLoaderData } from 'react-router'
import { type loader as rootLoader } from '#app/root.tsx'

function isUser(
	user: unknown,
): user is Awaited<ReturnType<typeof rootLoader>>['data']['user'] {
	return (
		typeof user === 'object' &&
		user !== null &&
		'id' in user &&
		typeof (user as { id: unknown }).id === 'string'
	)
}

export function useOptionalUser() {
	const data = useRouteLoaderData<typeof rootLoader>('root')
	if (!data || !isUser(data.user)) {
		return undefined
	}
	return data.user
}

export function useUser() {
	const maybeUser = useOptionalUser()
	if (!maybeUser) {
		throw new Error(
			'No user found in root loader, but user is required by useUser. If user is optional, try useOptionalUser instead.',
		)
	}
	return maybeUser
}

type Action = 'create' | 'read' | 'update' | 'delete' | 'export' | 'impersonate' | 'disable' | 'revoke'
type Entity =
	| 'user'
	| 'note'
	| 'role'
	| 'permission'
	| 'organization'
	| 'alert'
	| 'source'
	| 'subscription'
	| 'media-library'
	| 'pipeline'
	| 'audit-log'
	| 'article-report'
	| 'domain-block'
	| 'trial-invite'
	| 'plan-limit'
type Access = 'own' | 'any' | 'own,any' | 'any,own'
export type PermissionString =
	| `${Action}:${Entity}`
	| `${Action}:${Entity}:${Access}`

export function parsePermissionString(permissionString: PermissionString) {
	const [action, entity, access] = permissionString.split(':') as [
		Action,
		Entity,
		Access | undefined,
	]
	return {
		action,
		entity,
		access: access ? (access.split(',') as Array<Access>) : undefined,
	}
}

export function userHasPermission(
	user:
		| Pick<ReturnType<typeof useUser>, 'roles' | 'effectivePermissions'>
		| null
		| undefined,
	permission: PermissionString,
) {
	if (!user) return false
	const { action, entity, access } = parsePermissionString(permission)

	// Use effectivePermissions if available (includes role hierarchy + direct permissions)
	const permissions = user.effectivePermissions || []

	// Fallback to role permissions if effectivePermissions not available
	const allPermissions =
		permissions.length > 0
			? permissions
			: user.roles.flatMap((role) => role.permissions)

	return allPermissions.some(
		(p) =>
			p.entity === entity &&
			p.action === action &&
			(!access || access.includes(p.access)),
	)
}

/**
 * Returns the current user's timezone preference, or undefined if not logged in.
 */
export function useUserTimezone(): string | undefined {
	const user = useOptionalUser()
	return user?.timezone
}

export function userHasRole(
	user: Pick<ReturnType<typeof useUser>, 'roles'> | null,
	role: string,
) {
	if (!user) return false
	return user.roles.some((r) => r.name === role)
}
